import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import backend, { type HttpBackendOptions } from "i18next-http-backend";
import { initReactI18next } from "react-i18next";
import { LANGUAGE_LOCAL_STORAGE_KEY } from "src/constants";
import { TRANSLATION_ALL_NAMESPACE } from "src/i18n/config";
import { LanguageEnum } from "src/types/enum/LanguageEnum";
import axios from "src/utils/axios";
import { enGBTranslations } from "src/views/errors/translations/en-GB";
import { esESTranslations } from "src/views/errors/translations/es-ES";
import { frFRTranslations } from "src/views/errors/translations/fr-FR";
import { buildYupLocale } from "./yup";

const resources = {
    [LanguageEnum.FR]: frFRTranslations,
    [LanguageEnum.GB]: enGBTranslations,
    [LanguageEnum.ES]: esESTranslations,
};

const isStorybook = Boolean(import.meta.env.STORYBOOK);

// TRICK to call only once translation
const maxRetries = 3;
let currentRetry = 0;

const translationPromise = (async () => {
    const makeRequest = async (): Promise<any> => {
        try {
            if (!isStorybook && window.location.pathname !== "/error") {
                const { data: translation } = await axios.get(
                    `${import.meta.env.VITE_BASE_API_URL}translation`
                );
                return translation;
            } else {
                throw new TypeError("Directly load front resources");
            }
        } catch (err) {
            currentRetry++;
            if (currentRetry < maxRetries) {
                if (!isStorybook && window.location.pathname !== "/error") {
                    return new Promise((resolve) => setTimeout(() => resolve(makeRequest()), 2000));
                } else {
                    return true;
                }
            } else {
                if (!isStorybook && window.location.pathname !== "/error") {
                    window.location.href = "/error";
                }
                throw err;
            }
        }
    };

    return makeRequest();
})();

const backendOptions: HttpBackendOptions = {
    loadPath: `{{lng}}::{{ns}}`,
    request: async (_options, url, _payload, callback) => {
        try {
            const translations = await translationPromise;
            const [lng, ns] = url.split("::");
            callback(null, {
                status: 200,
                data: translations[lng][ns],
            });
        } catch {
            // Si le chargement des traductions échoue, utilisez les ressources locales.
            const [lng, ns] = url.split("::");
            callback(null, {
                status: 200,
                data: resources[lng][ns],
            });
        }
    },
};

i18n.use(LanguageDetector)
    .use(backend)
    .use(initReactI18next) // passes i18n down to react-i18next
    .init(
        {
            backend: backendOptions,
            ns: TRANSLATION_ALL_NAMESPACE,
            load: "currentOnly",
            interpolation: {
                escapeValue: false, // react already safes from xss
            },
            // cSpell: disable-next-line
            supportedLngs: Object.values(LanguageEnum),
            fallbackLng: LanguageEnum.GB,
            detection: {
                order: ["localStorage", "navigator"],
                lookupLocalStorage: LANGUAGE_LOCAL_STORAGE_KEY,
                caches: ["localStorage"],
            },
        },
        buildYupLocale
    );

i18n.services.formatter.add("lowercase", (value) => {
    return value.toLowerCase();
});
i18n.services.formatter.add("uppercase", (value) => {
    return value.toUpperCase();
});
i18n.services.formatter.add("capitalize", (value: string) => {
    if (!value) {
        return value;
    }
    return value[0].toLocaleUpperCase() + value.slice(1);
});

export default i18n;
